import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShareIcon from '@mui/icons-material/Share';
import { QRCodeCanvas } from 'qrcode.react';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { AccessBadge, buildSurveyPublicUrl, locationSummary } from './SurveyShareDialog';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "View link" modal from the manage list: every non-revoked link for the
 * survey (plain public link, location-scoped links, secure links), each in a
 * collapsible row that reveals its URL, copy button and QR code.
 *
 * The location-links endpoint returns location-scoped and secure links only;
 * the plain public link is the survey row's `active_public_token`, so it is
 * merged in here (deduped by token).
 *
 * Share collects a list of email addresses (delivery is wired up later).
 */
export default function SurveyLinkDialog({
  open,
  onClose,
  surveyId,
  surveyTitle,
  publicToken,
  onCopySuccess,
  onCopyError,
}) {
  const { apiFetch } = useSurveyRuntimeApi();
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [linksError, setLinksError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [shareSaved, setShareSaved] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setShareOpen(false);
    setEmails([]);
    setEmailError('');
    setShareSaved(false);
    setExpandedId(null);
    setLinksError('');
    if (!surveyId) {
      setLinks([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingLinks(true);
    // include_plain: every non-revoked link, the plain public one included.
    apiFetch(`/admin/surveys/${surveyId}/location-links?include_plain=true`)
      .then((data) => {
        if (!cancelled) setLinks(data?.links || []);
      })
      .catch(() => {
        if (!cancelled) {
          setLinks([]);
          setLinksError('Could not load the location and secure links for this survey.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingLinks(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, surveyId, apiFetch]);

  const allLinks = useMemo(() => {
    // Render nothing until the API has answered, so every link appears at
    // once instead of the survey row's own token showing up first.
    if (loadingLinks) return [];
    // Plain public link(s) first, then the rest as the API ordered them
    // (newest first).
    const isPlainPublic = (l) => !l.location_id && l.access_mode !== 'restricted';
    const rows = [...links.filter(isPlainPublic), ...links.filter((l) => !isPlainPublic(l))];
    // Fallback for an older runtime that ignores include_plain: the survey
    // row's active token is the plain public link.
    if (publicToken && !rows.some((l) => l.public_token === publicToken)) {
      rows.push({
        distribution_id: `plain-${publicToken}`,
        public_token: publicToken,
        access_mode: 'public',
        recipients: [],
        response_count: null, // not reported for the plain link
        client: '',
        country: '',
        city: '',
        building: '',
        floor: '',
        facility: '',
      });
    }
    return rows;
  }, [publicToken, links, loadingLinks]);

  // A survey with a single link: open it straight away, nothing to choose.
  useEffect(() => {
    if (!open || loadingLinks) return;
    if (allLinks.length === 1) setExpandedId(allLinks[0].distribution_id);
  }, [open, loadingLinks, allLinks]);

  function copyUrl(url) {
    void navigator.clipboard
      .writeText(url)
      .then(() => onCopySuccess?.())
      .catch(() => onCopyError?.());
  }

  function handleEmailsChange(next) {
    const invalid = next.find((e) => !EMAIL_RE.test(e));
    if (invalid) {
      setEmailError(`"${invalid}" is not a valid email address.`);
      return;
    }
    setEmailError('');
    setShareSaved(false);
    setEmails([...new Set(next.map((e) => e.trim().toLowerCase()))]);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Survey links</DialogTitle>
      <DialogContent>
        {surveyTitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Links for <strong>{surveyTitle}</strong>. Expand a link to see its URL and QR code.
          </Typography>
        )}

        {loadingLinks && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {linksError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
            {linksError}
          </Typography>
        )}

        {!loadingLinks && allLinks.length === 0 && !linksError && (
          <Typography variant="body2" color="text.secondary">
            No links have been generated for this survey yet.
          </Typography>
        )}

        {allLinks.map((link) => {
          const id = link.distribution_id;
          const url = buildSurveyPublicUrl(link.public_token, link.access_mode);
          const expanded = expandedId === id;
          const restricted = link.access_mode === 'restricted';
          return (
            <Accordion
              key={id}
              expanded={expanded}
              onChange={(_, isExpanded) => setExpandedId(isExpanded ? id : null)}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                mb: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`survey-link-${id}-content`}
                id={`survey-link-${id}-header`}
                sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { minWidth: 0, my: 1 } }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {locationSummary(link)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                    <AccessBadge link={link} />
                    {typeof link.response_count === 'number' && (
                      <Typography variant="caption" color="text.secondary">
                        {link.response_count} response{link.response_count === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {/* URL + copy */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    px: 1.25,
                    py: 0.75,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography
                    component="code"
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflowWrap: 'anywhere',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                    }}
                  >
                    {url}
                  </Typography>
                  <Tooltip title="Copy link">
                    <IconButton
                      size="small"
                      onClick={() => copyUrl(url)}
                      aria-label={`Copy link for ${locationSummary(link)}`}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {restricted && link.recipients?.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75 }}
                  >
                    Only {link.recipients.join(', ')} can open this link after signing in.
                  </Typography>
                )}

                {/* QR code */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 0.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px',
                      backgroundColor: '#ffffff',
                    }}
                    aria-label={`QR code for ${locationSummary(link)}`}
                  >
                    <QRCodeCanvas value={url} size={180} includeMargin={false} />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Share by email */}
        {!shareOpen ? (
          <Button
            startIcon={<ShareIcon fontSize="small" />}
            onClick={() => setShareOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Share
          </Button>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Share with people
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={emails}
              onChange={(e, next) => handleEmailsChange(next)}
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Type an email and press Enter…"
                  error={Boolean(emailError)}
                  helperText={emailError || 'Add one or more email addresses to share this survey with.'}
                />
              )}
            />
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                disabled={emails.length === 0}
                onClick={() => setShareSaved(true)}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#000000',
                  '&:hover': { backgroundColor: '#333333' },
                }}
              >
                Share with {emails.length || ''} {emails.length === 1 ? 'person' : 'people'}
              </Button>
              {shareSaved && (
                <Typography variant="caption" color="success.main">
                  Recipient list saved — email delivery is coming soon.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
